import { computed, watchEffect } from "vue";
import useAccessibilityStore from "@shared-stores/accessibility.store";
import { generateTtsId, speak as speakWithHighlight, speakText, setTtsRate, setTtsVoiceGender } from "@shared-utils/accessibility/tts.utils";

/**
 * Composable unifié pour la fonctionnalité TTS (Text-to-Speech)
 *
 * @param prefix - Préfixe pour générer un ID unique (optionnel)
 *
 * Retourne:
 * - isTtsEnabled: boolean - Indique si le TTS est activé
 * - speak(text): void - Lecture simple d'un texte (sans surlignage)
 * - speakFromElement(selector): void - Lecture d'un élément DOM avec surlignage mot par mot
 * - labelId: string - ID unique pour marquer un élément DOM (si prefix fourni)
 * - speakLabel(): void - Raccourci pour lire l'élément avec labelId
 */
export default function useTts(prefix?: string)
{
	const accessibilityStore = useAccessibilityStore();
	const isTtsEnabled = computed(() => accessibilityStore.tts);

	// Synchronise les paramètres TTS avec le store
	watchEffect(() =>
	{
		setTtsRate(accessibilityStore.ttsRate);
		setTtsVoiceGender(accessibilityStore.ttsVoice);
	});

	// ID unique pour le label (si prefix fourni)
	const labelId = prefix ? generateTtsId(prefix) : "";

	// État pour la lecture avec surlignage
	let originalContent: string | null = null;

	/**
	 * Lecture simple d'un texte (sans surlignage)
	 */
	const speak = (text: string) =>
	{
		speakText(text);
	};

	/**
	 * Lecture d'un élément DOM avec surlignage mot par mot
	 */
	const speakFromElement = (selector: string, bubbleSelector?: string) =>
	{
		originalContent = speakWithHighlight(originalContent, selector, bubbleSelector ?? selector);
	};

	/**
	 * Raccourci pour lire l'élément identifié par labelId
	 */
	const speakLabel = () =>
	{
		if (labelId)
		{
			const selector = "#" + labelId;
			originalContent = speakWithHighlight(originalContent, selector, selector);
		}
	};

	return {
		isTtsEnabled,
		speak,
		speakFromElement,
		labelId,
		speakLabel
	};
}
